import React, { useEffect, useState } from "react";
import { Logo } from "../Logo";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { scrollToId } from "../../hooks/useLenis";
import { logAnalytics } from "../../lib/api";

const links = [
    { id: "piattaforma", label: "La piattaforma" },
    { id: "funzionalita", label: "Funzionalità" },
    { id: "anteprima", label: "Anteprima" },
    { id: "evento", label: "Evento" },
    { id: "faq", label: "FAQ" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "bg-white/85 backdrop-blur-xl border-b border-navy-100" : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-[76px] flex items-center justify-between">
                <button
                    aria-label="SafetyDesk81 – home"
                    data-testid="header-logo"
                    onClick={() => go("hero")}
                    className="flex items-center"
                >
                    <Logo size="lg" />
                </button>

                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => go(l.id)}
                            data-testid={`nav-${l.id}`}
                            className="px-3 py-2 text-sm text-navy-800 hover:text-navy-950 relative group"
                        >
                            <span className="relative">
                                {l.label}
                                <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-gold-400 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                            </span>
                        </button>
                    ))}
                </nav>

                <div className="hidden md:flex items-center">
                    <button
                        data-testid="header-cta-register"
                        onClick={() => { logAnalytics("cta_click", { source: "header" }); go("iscriviti"); }}
                        className="group inline-flex items-center gap-2 bg-gold-400 hover:bg-gold-500 text-navy-950 font-semibold text-sm px-5 py-2.5 rounded-full transition-all duration-300 shadow-glow-gold hover:-translate-y-0.5"
                    >
                        Iscriviti all'evento
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
                            <path d="M5 12h14M13 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                <button
                    className="md:hidden w-10 h-10 rounded-full bg-navy-950 text-white flex items-center justify-center"
                    data-testid="header-menu-toggle"
                    aria-label="Apri menu"
                    onClick={() => setOpen((s) => !s)}
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key="mobile"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="md:hidden bg-white border-t border-navy-100 px-5 py-4"
                        data-testid="mobile-menu"
                    >
                        {links.map((l) => (
                            <button
                                key={l.id}
                                onClick={() => go(l.id)}
                                data-testid={`mnav-${l.id}`}
                                className="block w-full text-left py-3 border-b border-navy-100/60 text-navy-800 font-medium"
                            >
                                {l.label}
                            </button>
                        ))}
                        <button
                            data-testid="mheader-cta-register"
                            onClick={() => { logAnalytics("cta_click", { source: "mobile_menu" }); go("iscriviti"); }}
                            className="mt-4 w-full bg-gold-400 text-navy-950 font-semibold rounded-full py-3"
                        >
                            Iscriviti all'evento
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
